import { DeleteTwoTone, EditTwoTone } from "@ant-design/icons";
import { Dropdown, Menu, message, Popconfirm, Typography } from "antd";
import ListTable from "common/ListTable";
import {
  DAILY_PACKING_ASSIGNMENT_DELETE_ENDPOINT,
  DAILY_PACKING_ASSIGNMENT_LIST_ENDPOINT,
} from "apiServices/API_ENDPOINTS";
import { deleteData, getData } from "apiServices/common";
import AppContent from "common/AppContent";
import AppPageHeader from "common/PageHeader";
import ExtraContent from "components/Configuration/CommonComponents/ExtraContent";
import FilterComponent from "components/Configuration/CommonComponents/FilterComponent";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router";
import {
  DAILY_PACKING_ASSIGNMENT_CREATE_PERMISSION,
  DAILY_PACKING_ASSIGNMENT_DELETE_PERMISSION,
  DAILY_PACKING_ASSIGNMENT_EDIT_PERMISSION,
} from "routes/permissions";
import { cglFormatDate, hasPermission } from "utils/functions";

const PackingAssignmentList = () => {
  const location = useLocation();
  const routerPath = location ? location.pathname : null;
  const navigate = useNavigate();

  const [showFilter, setShowFilter] = useState(
    localStorage.getItem("filter_status") && routerPath
      ? JSON.parse(localStorage.getItem("filter_status"))[routerPath]
      : false,
  );
  const [refresh, setRefresh] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [tableData, setTableData] = useState([]);
  const [metaData, setMetaData] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const filterData = useSelector(({ filter }) => filter.filterData);
  const prevFilterDataRef = useRef(filterData);
  const currentPageRef = useRef(currentPage);

  const { Title } = Typography;

  useEffect(() => {
    currentPageRef.current = currentPage;
  }, [currentPage]);

  useEffect(() => {
    const filterStatus = localStorage.getItem("filter_status");
    const parsedFilterStatus = filterStatus ? JSON.parse(filterStatus) : {};
    parsedFilterStatus[routerPath] = !!showFilter;
    localStorage.setItem("filter_status", JSON.stringify(parsedFilterStatus));
  }, [showFilter, routerPath]);

  const fetchAssignments = useCallback(async (filterValues = null, page = null) => {
    setLoading(true);
    setError(null);
    const pageToUse = page ?? (filterValues ? 1 : currentPageRef.current);
    if (filterValues && page === null) {
      setCurrentPage(1);
      currentPageRef.current = 1;
    }

    const response = await getData(DAILY_PACKING_ASSIGNMENT_LIST_ENDPOINT, false, {
      page: pageToUse,
      plan_date: filterValues?.plan_date || "",
      packing_line_id: filterValues?.packing_line_id || "",
      sales_contract_id: filterValues?.sales_contract_id || "",
      buyer_id: filterValues?.buyer_id || "",
      buying_office_id: filterValues?.buying_office_id || "",
      sub_contract_factory_id: filterValues?.sub_contract_factory_id || "",
      po_map_id: filterValues?.po_map_id || "",
    });

    if (response?.status === 200 || response?.data?.code === 200) {
      const paginated = response?.data?.data;
      setTableData(paginated?.data || []);
      setMetaData({
        defaultPageSize: paginated?.per_page || 10,
        current: paginated?.current_page || pageToUse,
        defaultCurrent: paginated?.current_page || pageToUse,
        total: paginated?.total || 0,
      });
      setCurrentPage(paginated?.current_page || pageToUse);
      currentPageRef.current = paginated?.current_page || pageToUse;
    } else {
      message.error("Error getting packing assignment data");
      setError("Could not load this list.");
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    const isFilterChanged = prevFilterDataRef.current !== filterData && prevFilterDataRef.current !== null;
    if (isFilterChanged) {
      prevFilterDataRef.current = filterData;
      fetchAssignments(filterData, 1);
    } else {
      prevFilterDataRef.current = filterData;
      fetchAssignments(filterData, currentPageRef.current);
    }
  }, [fetchAssignments, filterData, refresh]);

  const onAdd = () => navigate("/digitalization/packing/assignment/create");
  const onEdit = (id) => navigate(`/digitalization/packing/assignment/edit/${id}`);

  const onDelete = async (id) => {
    setLoading(true);
    const response = await deleteData(`${DAILY_PACKING_ASSIGNMENT_DELETE_ENDPOINT}/${id}`);
    if (response?.code === 200 || response?.status === 200) {
      message.success(response?.message || "Packing Assignment Deleted Successfully");
      setRefresh((prev) => prev + 1);
    } else {
      message.error("Error deleting packing assignment");
    }
    setLoading(false);
  };

  const onPageChange = (newPage) => {
    setCurrentPage(newPage);
    currentPageRef.current = newPage;
    fetchAssignments(filterData, newPage);
  };

  const tableColumns = [
    {
      title: "Plan Date",
      dataIndex: "plan_date",
      key: "plan_date",
      render: (v) => (v ? cglFormatDate(v) : "-"),
    },
    {
      title: "PO",
      key: "po",
      render: (_, record) => record?.po_map?.original_po || "-",
    },
    {
      title: "Sales Contract",
      key: "sales_contract",
      render: (_, record) => record?.sales_contract?.reference_no || "-",
    },
    {
      title: "Buyer",
      key: "buyer",
      render: (_, record) => record?.buyer?.name || "-",
    },
    {
      title: "Buying Office / Factory",
      key: "office_factory",
      render: (_, record) => record?.buying_office?.name || record?.sub_contract_factory?.name || "-",
    },
    {
      title: "Packing Line",
      key: "packing_line",
      render: (_, record) => record?.packing_line?.name || "-",
    },
    {
      title: "Assigned",
      key: "details_count",
      render: (_, record) => record?.details?.length || 0,
    },
    {
      title: "Action",
      key: "action",
      width: "10%",
      render: (_, record) => (
        <Dropdown.Button
          overlay={
            <Menu
              items={[
                {
                  key: "edit",
                  disabled: !hasPermission([DAILY_PACKING_ASSIGNMENT_EDIT_PERMISSION]),
                  label: (
                    <div onClick={() => onEdit(record?.id)} style={{ cursor: "pointer" }}>
                      <EditTwoTone /> <span style={{ marginLeft: 5 }}>Edit</span>
                    </div>
                  ),
                },
                {
                  key: "delete",
                  disabled: !hasPermission([DAILY_PACKING_ASSIGNMENT_DELETE_PERMISSION]),
                  label: (
                    <Popconfirm title="Are you sure you want to delete?" onConfirm={() => onDelete(record?.id)}>
                      <DeleteTwoTone />
                      <span style={{ color: "red", marginLeft: 5 }}>Delete</span>
                    </Popconfirm>
                  ),
                },
              ]}
            />
          }
        />
      ),
    },
  ];

  const pageHeader = useMemo(
    () => (
      <AppPageHeader
        extra={
          <ExtraContent
            refresh={refresh}
            setRefresh={setRefresh}
            handleAddItemOpen={onAdd}
            disabled={!hasPermission([DAILY_PACKING_ASSIGNMENT_CREATE_PERMISSION])}
            addButtonText={hasPermission([DAILY_PACKING_ASSIGNMENT_CREATE_PERMISSION]) ? "Add New" : "No Permission"}
            setShowFilter={setShowFilter}
            showFilter={showFilter}
          />
        }
        title={<Title level={5}>Packing Assignment</Title>}
      />
    ),
    [refresh, showFilter],
  );

  const memoizedFilter = useMemo(
    () => (
      <FilterComponent
        tableData={tableData}
        filterAPI={fetchAssignments}
        filterName="daily_packing_assignment"
        showFilter={showFilter}
        setShowFilter={setShowFilter}
      />
    ),
    [showFilter],
  );

  return (
    <AppContent
      header={pageHeader}
      content={
        <>
          {memoizedFilter}
          <div className="table" style={{ marginTop: 10 }}>
            <ListTable
              rowKey="id"
              loading={loading}
              error={error}
              onRetry={() => fetchAssignments(filterData, currentPageRef.current)}
              emptyText="No records to show yet."
              dataSource={tableData}
              columns={tableColumns}
              pagination={{
                showSizeChanger: false,
                defaultPageSize: metaData?.defaultPageSize,
                current: metaData?.current,
                onChange: onPageChange,
                defaultCurrent: metaData?.defaultCurrent,
                total: metaData?.total,
              }}
            />
          </div>
        </>
      }
    />
  );
};

export default PackingAssignmentList;
